import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import routes from "@/constants/routes";
import Button from "@/atoms/Button";
import Input from "@/atoms/Input";
import RightArrow from "@/public/icons/arrow-right.svg";
import RightArrowDisabled from "@/public/icons/arrow-right-grey-400.svg";
import CheckBlueIcon from "@/public/icons/check-bg-blue-md.svg";
import FormHeader from "@/components/shared/FormHeader";
import { generateMagicLink } from "@/services/api";

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const MagicLink = () => {
  const router = useRouter();
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { redirect, email: emailParam } = router.query;
  const redirectParam = typeof redirect === "string" ? redirect : undefined;

  const getMagicLink = async (email: string) => {
    if (loading) return;
    setLoading(true);
    setError("");

    try {
      await generateMagicLink(email, redirectParam);
      router.push(`${routes.magicLink.checkEmail}?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    getMagicLink(email);
  };

  useEffect(() => {
    if (router.isReady && typeof emailParam === "string" && emailParam) {
      setEmail(emailParam);
      setTouched(true);
    }
  }, [router.isReady, emailParam]);

  return (
    <div className="mx-2 sm:mx-6 md:mx-10 xl:mx-0 pt-16 md:pt-0">
      {router.isReady && (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col max-w-2xl xl:max-w-3xl mx-auto text-3xl"
        >
          <FormHeader title="Hello there, please log in" image="/icons/key.svg" />

          <div className="form-container rounded-2xl !py-4 md:!py-6">
            <Input
              type="email"
              name="email"
              label="Email address"
              placeholder="e.g. julie.smith@mail.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              onBlur={() => setTouched(true)}
              autoComplete="email"
              className="rounded-[40px]"
              suffix={touched && !error && isValidEmail(email) ? <CheckBlueIcon /> : null}
              iconPosition="right-6 top-6"
              error={error && touched ? error : ""}
            />
          </div>

          <div className="flex flex-row-reverse flex-wrap justify-between sm:flex-nowrap gap-1 mt-10 md:mt-16">
            <Button
              type="submit"
              disabled={loading || !isValidEmail(email)}
              suffix={
                loading || !isValidEmail(email) ? (
                  <RightArrowDisabled className={`ml-3`} />
                ) : (
                  <RightArrow className={`ml-3  stroke-brown-200`} />
                )
              }
              className="rounded-[40px]"
            >
              Send me a magic link
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              className="rounded-[40px]"
              onClick={() => router.push(routes.register)}
            >
              Need an account? Register here.
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MagicLink;
